// src/components/zoom/ZoomRoomSettingsModal.jsx
import React, { useEffect, useState } from 'react';
import { FiX, FiSliders } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { getZoomRoomSettings, saveZoomRoomSettings } from '../../services/zoomApiService';

const Toggle = ({ label, hint, checked, onChange }) => (
  <label className="flex items-start justify-between gap-4 py-2 cursor-pointer">
    <span>
      <span className="text-sm font-medium text-gray-700">{label}</span>
      {hint && <span className="block text-[11px] text-gray-400">{hint}</span>}
    </span>
    <input type="checkbox" className="mt-1 h-4 w-4 accent-blue-600" checked={!!checked} onChange={e => onChange(e.target.checked)} />
  </label>
);

const ZoomRoomSettingsModal = ({ workspaceId, onClose }) => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    getZoomRoomSettings(workspaceId).then(setS).catch((err) => {
      console.error(err);
      setLoadError(true);
      toast.error('No se pudieron cargar los ajustes');
    });
  }, [workspaceId]);

  const set = (k) => (v) => setS(prev => ({ ...prev, [k]: v }));
  const input = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400';

  const handleSave = async () => {
    if (!String(s.password || '').trim()) { toast.error('La contraseña no puede estar vacía'); return; }
    setSaving(true);
    try { await saveZoomRoomSettings(workspaceId, s); toast.success('Ajustes guardados'); onClose(); }
    catch (err) { console.error(err); toast.error('No se pudieron guardar los ajustes'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2"><FiSliders /> Ajustes de creación</h3>
            <p className="text-xs text-gray-500 mt-0.5">Se aplican a todas las salas nuevas que cree el Gestor.</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><FiX size={18} /></button>
        </div>
        {loadError ? (
          <div className="p-10 text-center text-sm text-red-600">No se pudieron cargar los ajustes. Cierra y vuelve a intentar.</div>
        ) : !s ? (
          <div className="p-10 text-center text-sm text-gray-400">Cargando…</div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Contraseña de las salas</label>
              <input className={input} value={s.password} onChange={e => set('password')(e.target.value)} maxLength={10} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre sala Masterclass</label>
                <input className={input} value={s.topicMC} onChange={e => set('topicMC')(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre sala Clase</label>
                <input className={input} value={s.topicClase} onChange={e => set('topicClase')(e.target.value)} />
              </div>
            </div>
            <p className="text-[11px] text-gray-400 -mt-2">{'{curso}'} se reemplaza por el nombre del curso. Mantén "MASTERCLASS" en el nombre de MC: el bot lo usa para clasificar las grabaciones.</p>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Grabación automática</label>
              <select className={input} value={s.autoRecording} onChange={e => set('autoRecording')(e.target.value)}>
                <option value="cloud">En la nube (la recoge el bot)</option>
                <option value="local">En la computadora</option>
                <option value="none">Desactivada</option>
              </select>
            </div>
            <div className="divide-y divide-gray-100">
              <Toggle label="Permitir entrar antes que el anfitrión" hint="Desactivado: nadie entra hasta que el anfitrión inicia." checked={s.joinBeforeHost} onChange={set('joinBeforeHost')} />
              <Toggle label="Sala de espera" checked={s.waitingRoom} onChange={set('waitingRoom')} />
              <Toggle label="Silenciar al entrar" checked={s.muteUponEntry} onChange={set('muteUponEntry')} />
              <Toggle label="Video del anfitrión encendido" checked={s.hostVideo} onChange={set('hostVideo')} />
              <Toggle label="Video de participantes encendido" checked={s.participantVideo} onChange={set('participantVideo')} />
              <Toggle label="Crear salas al crear un curso" checked={s.autoCreateOnCourse} onChange={set('autoCreateOnCourse')} />
              <Toggle label='Ofrecer "Crear salas faltantes" al distribuir' checked={s.autoCreateOnDistribute} onChange={set('autoCreateOnDistribute')} />
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
          <button onClick={handleSave} disabled={!s || loadError || saving} className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ZoomRoomSettingsModal;
